import type { EndorsementOption } from '@/api/types/enrollment'
import type { EligibilityFormData } from '../types'

type EndorsementSelectionProps = {
  endorsementOptions: EndorsementOption[]
  disabled?: boolean
  formData: EligibilityFormData
  onToggleEndorsement: (value: string) => void
  onOpenDetails: (endorsement: EndorsementOption) => void
}

export function EndorsementSelection({
  endorsementOptions,
  disabled = false,
  formData,
  onToggleEndorsement,
  onOpenDetails
}: EndorsementSelectionProps) {
  return (
    <div className="animate-fade-in-up" style={{ animationDelay: '200ms' }}>
      <p className="modal-heading__subtitle mb-3 text-left">
        Select one that you may be interested in the future.
      </p>

      <div className="space-y-1.5 md:hidden">
        {endorsementOptions.map(({ type, label }) => {
          const value = type
          const isChecked = (formData.endorsements || []).includes(value)

          return (
            <label key={value} className="flex cursor-pointer items-center gap-2.5">
              <div className="relative flex items-center">
                <input
                  type="checkbox"
                  checked={isChecked}
                  disabled={disabled}
                  onChange={() => onToggleEndorsement(value)}
                  className="pointer-events-none absolute h-0 w-0 opacity-0"
                />
                <div
                  className={`flex h-5 w-5 items-center justify-center rounded-[4px] border-2 transition-colors ${
                    isChecked
                      ? 'border-primary bg-primary shadow-[0_0_0_3px_rgba(230,83,0,0.15)]'
                      : 'border-gray-lighter bg-white'
                  }`}
                >
                  {isChecked ? (
                    <svg
                      className="h-3 w-3 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  ) : null}
                </div>
              </div>
              <span className="text-sm text-gray-900">{label}</span>
            </label>
          )
        })}
      </div>

      <div className="hidden animate-fade-in-up md:block" style={{ animationDelay: '220ms' }}>
        <table className="w-full overflow-hidden rounded-xl border border-gray-200 text-xs">
          <thead className="bg-primary text-white">
            <tr>
              <th className="w-[70px] px-2 py-2 text-center text-[11px] font-medium md:text-xs">
                Select
              </th>
              <th className="border-l border-[#d98a4d] px-2 py-2 text-left text-[11px] font-medium md:text-xs">
                Endorsement
              </th>
              <th className="border-l border-[#d98a4d] px-2 py-2 text-left text-[11px] font-medium md:text-xs">
                Requirements
              </th>
              <th className="w-[130px] border-l border-[#d98a4d] px-2 py-2 text-left text-[11px] font-medium md:text-xs">
                Details
              </th>
            </tr>
          </thead>
          <tbody>
            {endorsementOptions.map(endorsement => {
              const isChecked = (formData.endorsements || []).includes(endorsement.type)

              return (
                <tr
                  key={endorsement.type}
                  className="border-t border-gray-100 bg-white hover:bg-gray-50"
                >
                  <td className="px-2 py-2 text-center align-middle">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      disabled={disabled}
                      onChange={() => onToggleEndorsement(endorsement.type)}
                      className="shrink-0"
                    />
                  </td>
                  <td className="px-3 py-2 text-left text-gray-900">{endorsement.label}</td>
                  <td className="px-3 py-2 text-left text-gray-900">
                    {endorsement.requirements}
                  </td>
                  <td className="px-2 py-2 text-center align-middle">
                    <p
                      onClick={() => onOpenDetails(endorsement)}
                      className="cursor-pointer text-[11px] font-semibold text-[#E65300] underline underline-offset-2 transition-colors hover:text-[#cc4900]"
                    >
                      View Description
                    </p>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
